/**
 * Mega menu tracking (GFTY-5575)
 *
 * One delegated listener for every mega menu click and panel open, on desktop and mobile.
 * It reads only the tracking DOM contract, so the legacy menu and the v2.1 nav (GFTY-5574)
 * are tracked the same way and the nav scripts never touch the dataLayer:
 *
 *   data-mm-root      on the <nav>
 *   data-mm-panel     on each panel root ("location", drill-down "location/arizona");
 *                     on an entry, the panel it opens
 *   data-mm-type      on every trackable link/button: entry | key_market | link | heading | pod
 *   data-mm-list      optional, the group inside the panel ("popular", "all_locations", "cities")
 *   data-mm-position  1-based index within its list (computed from the DOM when missing)
 *   data-mm-label     optional label override (default: the trimmed text)
 *
 *   The nav dispatches `mega-menu:panel-open` (bubbles, detail: { panel, level }) on the panel
 *   element when a panel (level 1) or drill-down (level 2) opens.
 *
 * Events go through pushDataLayerForMegaMenu (assets/dataLayer.js) to GA4, which exports to
 * BigQuery: `mega_menu_click` and `mega_menu_panel_open`. Every payload carries every mm_* key
 * (null when unknown), because GTM's data model keeps keys from earlier pushes and a panel open
 * would otherwise inherit the previous click's label and URL. Only menu content and the page path
 * are sent: no query strings from the page, nothing typed by the user.
 *
 * Tested in tests/mega-menu-tracking.test.js.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  } else if (root && root.document) {
    root.GiftoryMegaMenuTracking = api;
    api.init(root.document, root);
  }
})(typeof window !== 'undefined' ? window : this, function () {
  'use strict';

  var EVENT_CLICK = 'mega_menu_click';
  var EVENT_PANEL_OPEN = 'mega_menu_panel_open';
  var PANEL_OPEN_EVENT = 'mega-menu:panel-open';
  var TYPES = ['entry', 'key_market', 'link', 'heading', 'pod'];
  // Tailwind `2md` (tailwind.config.js): the menu switches to its desktop layout at 991px.
  var DESKTOP_QUERY = '(min-width: 991px)';
  // GA4 truncates event parameter values at 100 characters.
  var MAX_VALUE_LENGTH = 100;
  // A double click, or a handler re-firing on the same element, inside this window is one event.
  var CLICK_DEDUPE_MS = 800;
  var PANEL_DEDUPE_MS = 1000;

  function clean(value) {
    if (value === null || value === undefined) return null;
    var text = String(value).replace(/\s+/g, ' ').trim();
    return text ? text.slice(0, MAX_VALUE_LENGTH) : null;
  }

  // Same shape as Liquid's `handleize`, for entries that open no panel.
  function handleize(value) {
    var text = clean(value);
    if (!text) return null;
    var handle = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    return handle || null;
  }

  function toPosition(value) {
    var number = parseInt(value, 10);
    return number > 0 ? number : null;
  }

  function toLevel(value) {
    var number = parseInt(value, 10);
    return number === 1 || number === 2 ? number : null;
  }

  // Same-origin links are sent as path + query so they fit GA4's 100 characters; buttons,
  // "#" and javascript: links have no destination.
  function linkUrl(element, origin) {
    var href = element.getAttribute('href');
    if (!href) return null;
    href = href.trim();
    if (!href || href.charAt(0) === '#' || /^javascript:/i.test(href)) return null;
    try {
      var url = new URL(href, origin);
      if (url.origin === origin) return clean(url.pathname + url.search);
      return clean(url.origin + url.pathname + url.search);
    } catch (e) {
      return clean(href);
    }
  }

  function panelElementOf(element) {
    return element.closest('[data-mm-panel]');
  }

  function panelOf(element) {
    var panelElement = panelElementOf(element);
    return panelElement ? clean(panelElement.getAttribute('data-mm-panel')) : null;
  }

  function entryOf(panel, type, label) {
    if (panel) return panel.split('/')[0] || null;
    return type === 'entry' ? handleize(label) : null;
  }

  function labelOf(element) {
    var override = clean(element.getAttribute('data-mm-label'));
    return override || clean(element.textContent);
  }

  // data-mm-position when set, else the element's index among the trackable items of the same
  // list and type in the same panel (or in the nav root, for items outside any panel).
  function positionOf(element) {
    var explicit = toPosition(element.getAttribute('data-mm-position'));
    if (explicit) return explicit;

    var panelElement = panelElementOf(element);
    var scope = panelElement || element.closest('[data-mm-root]');
    if (!scope) return null;

    var list = element.getAttribute('data-mm-list');
    var type = element.getAttribute('data-mm-type');
    var items = scope.querySelectorAll('[data-mm-type]');
    var index = 0;
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (item.getAttribute('data-mm-type') !== type) continue;
      if (item.getAttribute('data-mm-list') !== list) continue;
      if (panelElementOf(item) !== panelElement) continue;
      index++;
      if (item === element) return index;
    }
    return null;
  }

  function buildClickPayload(element, context) {
    var type = element.getAttribute('data-mm-type');
    if (TYPES.indexOf(type) === -1) return null;
    var label = labelOf(element);
    var panel = panelOf(element);
    return {
      mm_type: type,
      mm_entry: entryOf(panel, type, label),
      mm_panel: panel,
      mm_level: null,
      mm_list: clean(element.getAttribute('data-mm-list')),
      mm_position: positionOf(element),
      mm_label: label,
      mm_url: linkUrl(element, context.origin),
      mm_device: context.device,
      mm_page_template: context.pageTemplate,
      mm_page_path: context.pagePath,
    };
  }

  function buildPanelOpenPayload(detail, context) {
    var panel = clean(detail && detail.panel);
    if (!panel) return null;
    return {
      mm_type: null,
      mm_entry: entryOf(panel),
      mm_panel: panel,
      mm_level: toLevel(detail.level) || (panel.indexOf('/') === -1 ? 1 : 2),
      mm_list: null,
      mm_position: null,
      mm_label: null,
      mm_url: null,
      mm_device: context.device,
      mm_page_template: context.pageTemplate,
      mm_page_path: context.pagePath,
    };
  }

  function createDeduper(windowMs, now) {
    var last = {};
    return function shouldSend(key) {
      var time = now();
      if (last[key] !== undefined && time - last[key] < windowMs) return false;
      last[key] = time;
      return true;
    };
  }

  function pageContext(win, doc) {
    var device = null;
    try {
      device = win.matchMedia(DESKTOP_QUERY).matches ? 'desktop' : 'mobile';
    } catch (e) {
      device = null;
    }
    // layout/theme.liquid renders <body class="... template-{{ template.name }}">.
    var match = doc.body && /(?:^|\s)template-([\w-]+)/.exec(doc.body.className || '');
    return {
      origin: win.location.origin,
      device: device,
      pageTemplate: match ? match[1] : null,
      pagePath: clean(win.location.pathname),
    };
  }

  function defaultSend(win) {
    return function (eventName, params) {
      if (typeof win.pushDataLayerForMegaMenu === 'function') {
        win.pushDataLayerForMegaMenu(eventName, params);
      }
    };
  }

  function init(doc, win, options) {
    if (!doc || doc.__megaMenuTracking) return;
    doc.__megaMenuTracking = true;
    options = options || {};
    var send = options.send || defaultSend(win);
    var now = options.now || Date.now;
    var clickIsNew = createDeduper(CLICK_DEDUPE_MS, now);
    var panelIsNew = createDeduper(PANEL_DEDUPE_MS, now);

    // A link that opens a drill-down (a state with cities) only leaves the page when the nav
    // lets it: a tap that opens its cities prevents the default (assets/mega-menu.js). That is
    // known once the nav's handler on document has run, so the click is built in the capture
    // phase, before any handler can stop it, and sent from window, after them. It then has no
    // URL: the page was not visited, and its mega_menu_panel_open says what opened.
    var pending = null;
    function finish() {
      if (!pending) return;
      var click = pending;
      pending = null;
      try {
        if (click.event.defaultPrevented && click.element.getAttribute('aria-controls')) {
          click.payload.mm_url = null;
        }
        send(EVENT_CLICK, click.payload);
      } catch (e) {
        // Never let tracking break the click it observes.
      }
    }
    var deferred = typeof win.addEventListener === 'function' && typeof win.setTimeout === 'function';
    if (deferred) {
      win.addEventListener('click', function (event) {
        if (pending && pending.event === event) finish();
      });
    }

    doc.addEventListener(
      'click',
      function (event) {
        try {
          var target = event.target;
          if (!target || typeof target.closest !== 'function') return;
          var element = target.closest('[data-mm-type]');
          if (!element) return;
          var payload = buildClickPayload(element, pageContext(win, doc));
          if (!payload) return;
          var key = [payload.mm_type, payload.mm_panel, payload.mm_list, payload.mm_position, payload.mm_label].join('|');
          if (!clickIsNew(key)) return;
          finish();
          pending = { event: event, element: element, payload: payload };
          if (!deferred) return finish();
          // If a handler stops the click before window, send it on the next tick.
          win.setTimeout(finish, 0);
        } catch (e) {
          // Never let tracking break the click it observes.
        }
      },
      true
    );

    doc.addEventListener(PANEL_OPEN_EVENT, function (event) {
      try {
        var payload = buildPanelOpenPayload(event.detail, pageContext(win, doc));
        if (!payload) return;
        if (!panelIsNew(payload.mm_panel + '|' + payload.mm_level)) return;
        send(EVENT_PANEL_OPEN, payload);
      } catch (e) {
        // Never let tracking break the menu.
      }
    });
  }

  return {
    EVENT_CLICK: EVENT_CLICK,
    EVENT_PANEL_OPEN: EVENT_PANEL_OPEN,
    PANEL_OPEN_EVENT: PANEL_OPEN_EVENT,
    DESKTOP_QUERY: DESKTOP_QUERY,
    CLICK_DEDUPE_MS: CLICK_DEDUPE_MS,
    PANEL_DEDUPE_MS: PANEL_DEDUPE_MS,
    clean: clean,
    handleize: handleize,
    linkUrl: linkUrl,
    panelOf: panelOf,
    positionOf: positionOf,
    buildClickPayload: buildClickPayload,
    buildPanelOpenPayload: buildPanelOpenPayload,
    createDeduper: createDeduper,
    pageContext: pageContext,
    init: init,
  };
});
